import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { Navigate } from 'react-router-dom';
import { getComplianceExpiry, GetComplianceExpiryOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShieldAlert, AlertTriangle, Clock, CheckCircle, Mail, Ban } from 'lucide-react';

type Item = GetComplianceExpiryOutputType['expiryItems'][0];

const statusConfig: Record<string, { label: string; color: string; icon: React.FC<{className?:string}> }> = {
  overdue: { label: 'Overdue', color: 'bg-destructive/15 text-destructive', icon: AlertTriangle },
  expired: { label: 'Expired', color: 'bg-destructive/15 text-destructive', icon: AlertTriangle },
  due_soon: { label: 'Due Soon', color: 'bg-accent/20 text-accent-foreground', icon: Clock },
  expiring_soon: { label: 'Expiring Soon', color: 'bg-accent/20 text-accent-foreground', icon: Clock },
};

export default function ComplianceExpiry() {
  const { user } = useAuth();
  const [data, setData] = useState<GetComplianceExpiryOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!user) return;
    getComplianceExpiry({}).then(setData).catch(console.error).finally(() => setLoading(false));
  }, [user]);

  if (!['Manager', 'Admin', 'Owner'].includes(user?.role || '')) return <Navigate to="/" replace />;

  if (loading) return (
    <div className="px-4 md:px-8 py-6 max-w-4xl mx-auto space-y-4">
      <Skeleton className="h-8 w-64" />
      {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16" />)}
    </div>
  );

  const filtered = data?.expiryItems.filter(i => {
    if (filter === 'all') return true;
    if (filter === 'overdue') return i.status === 'overdue' || i.status === 'expired';
    if (filter === 'due_soon') return i.status === 'due_soon' || i.status === 'expiring_soon';
    if (filter === 'submission') return i.type === 'submission';
    if (filter === 'certification') return i.type === 'certification';
    return true;
  }) || [];

  return (
    <div className="px-4 md:px-8 py-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <ShieldAlert className="w-5 h-5 text-primary" />
        <h1 className="text-xl font-bold text-foreground">Compliance Expiry & Reminder Preview</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Preview who would receive reminders. <strong>Sending is disabled</strong> — this is a read-only preview queue.
      </p>

      {/* Summary */}
      {data && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-destructive">{data.summary.overdue}</p>
            <p className="text-xs text-muted-foreground">Overdue / Expired</p>
          </div>
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-accent-foreground">{data.summary.dueSoon}</p>
            <p className="text-xs text-muted-foreground">Due / Expiring Soon</p>
          </div>
          <div className="bg-muted border border-border rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-foreground">{data.summary.total}</p>
            <p className="text-xs text-muted-foreground">Total Items</p>
          </div>
        </div>
      )}

      {/* Disabled banner */}
      <div className="flex items-center gap-2 bg-muted border border-border rounded-lg px-4 py-3 mb-4">
        <Ban className="w-4 h-4 text-muted-foreground shrink-0" />
        <p className="text-xs text-muted-foreground">
          <strong>Automatic sending is OFF.</strong> No emails, SMS, or notifications will be sent. This queue shows what <em>would</em> be sent if reminders were activated. Contact an admin to enable sending in a future sprint.
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All ({data?.summary.total || 0})</SelectItem>
            <SelectItem value="overdue">Overdue / Expired</SelectItem>
            <SelectItem value="due_soon">Due / Expiring Soon</SelectItem>
            <SelectItem value="submission">Submissions Only</SelectItem>
            <SelectItem value="certification">Certifications Only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Items */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-lg">
          <CheckCircle className="w-8 h-8 text-primary mx-auto mb-2 opacity-40" />
          <p className="text-sm text-muted-foreground">No compliance items match this filter.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(item => {
            const config = statusConfig[item.status] || statusConfig.due_soon;
            const Icon = config.icon;
            return (
              <div key={item.id} className="border border-border rounded-lg p-4 bg-card">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="text-sm font-semibold text-foreground">{item.staffName}</p>
                      <Badge variant="secondary" className={`text-xs ${config.color}`}>
                        <Icon className="w-3 h-3 mr-1" />{config.label}
                      </Badge>
                      <Badge variant="secondary" className="text-xs">{item.type === 'submission' ? 'Compliance' : 'Certificate'}</Badge>
                    </div>
                    <p className="text-sm text-foreground">{item.itemName}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      <span>Due: {item.dueDate}</span>
                      <span>{item.daysUntilDue < 0 ? `${Math.abs(item.daysUntilDue)} days overdue` : `${item.daysUntilDue} days remaining`}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Mail className="w-3 h-3" />
                      <span className="line-through">{item.staffEmail}</span>
                    </div>
                    <p className="text-xs text-muted-foreground italic mt-0.5">Would receive reminder</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
